import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { atlasProjectionMode } from '../services/atlasProjectionContext';
import { useAtlasDraftSaveRecoveryStore } from '../stores/atlasDraftSaveRecoveryStore';
import { isOrgPlanningReviewScopeCurrent } from './useOrgPlanningReviewScope';
import type { OrgPlanningScenarioProjection } from '../models/OrgPlanningScenarioProjection';
import type {
  OrganizationChangeApplyResult,
  OrganizationChangeApplyState,
} from '../models/OrganizationChangeApplyResult';
import {
  recallSelectedOrgPlanScenarioId,
  useOrgPlanningScenarioStore,
} from '../stores/orgPlanningScenarioStore';

export type { OrgPlanningScenarioProjection } from '../models/OrgPlanningScenarioProjection';

/**
 * Background discovery cadence for scenarios opened elsewhere (another tab or
 * collaborator). Reload-time rediscovery is already covered by the mount fetch
 * and `refetchOnWindowFocus`, and every local mutation invalidates explicitly,
 * so this interval only has to catch external drafts. At 15s it was re-running
 * two multi-second permissioned reads on every idle chart — 60 organization-plan
 * requests in a single session with nothing pending.
 */
const SCENARIO_DISCOVERY_POLL_MS = 60_000;

/**
 * Persistent failures must not poll at full cadence forever — on stage,
 * expired-session tabs kept retrying these endpoints indefinitely, thousands
 * of failing requests a day per diagram. But this heads query is also how an
 * open draft is REDISCOVERED after a reload, and stage throws *transient*
 * 401/403s (entitlement-resolution hiccups, auth settling right after
 * refresh). Treating those as terminal made the draft vanish until the next
 * manual reload (QA issue 78). So: only 404 (missing head) and 409 (superseded plan) are terminal;
 * every other failure gets one quick retry and then keeps polling at an
 * exponentially backed-off cadence (capped at 15min) — a real permission
 * denial costs a handful of requests per hour instead of a lost draft.
 */
const TERMINAL_HTTP_STATUSES = new Set([404, 409]);
const TERMINAL_OPERATIONAL_STATES = new Set<OrganizationChangeApplyState>([
  'applied',
  'reconciled',
  'failed',
  'conflict',
]);

function statusOf(error: unknown): number | undefined {
  return (error as (Error & { status?: number }) | null)?.status;
}

function boundedRetry(attempt: number, error: Error): boolean {
  const status = statusOf(error);
  return !(status !== undefined && TERMINAL_HTTP_STATUSES.has(status)) && attempt < 2;
}

function backedOffInterval(query: {
  state: { error: Error | null; fetchFailureCount: number };
}): number | false {
  const status = statusOf(query.state.error);
  if (status !== undefined && TERMINAL_HTTP_STATUSES.has(status)) return false;
  const failures = query.state.fetchFailureCount;
  if (failures === 0) return SCENARIO_DISCOVERY_POLL_MS;
  return Math.min(SCENARIO_DISCOVERY_POLL_MS * 2 ** Math.min(failures, 4), 15 * 60_000);
}

/**
 * TanStack Query's refetch interval contract is milliseconds or false. Keep
 * that policy explicit while the canonical Apply receipt is non-terminal.
 */
export function operationalStatusRefetchInterval(query: {
  state: {
    data?: Pick<OrganizationChangeApplyResult, 'state'>;
    error: Error | null;
    fetchFailureCount: number;
  };
}): number | false {
  const state = query.state.data?.state;
  if (state && TERMINAL_OPERATIONAL_STATES.has(state)) return false;
  return backedOffInterval(query);
}

export const orgPlanningScenarioProjectionKey = (
  folderId: string,
  diagramId: string,
  scenarioId: string | undefined,
  headChangeSetId: string | undefined,
  projectionKind: 'position' | 'people',
) => [
  'atlas',
  'organization-plan-projection',
  folderId,
  diagramId,
  scenarioId,
  headChangeSetId,
  projectionKind,
] as const;

export function shouldSettleSubmittedCanvasScenario(
  submittedScenarioId: string | undefined,
  heads: readonly { scenarioId: string }[],
  submittedOperationalChangeSetId?: string,
): boolean {
  return Boolean(
    submittedScenarioId
    && !submittedOperationalChangeSetId
    && !heads.some((head) => head.scenarioId === submittedScenarioId),
  );
}

export function shouldSettleOperationalCanvasScenario(
  state: OrganizationChangeApplyState | undefined,
): boolean {
  return state === 'applied' || state === 'reconciled';
}

/**
 * Permissioned post-state for the scenario explicitly selected on one diagram.
 * The response is disposable query state; it is never copied into collaborative
 * canvas state.
 */
export function useOrgPlanningScenarioProjection(input: {
  folderId: string | null | undefined;
  diagramId: string | null | undefined;
  projectionKind: 'position' | 'people';
  enabled?: boolean;
}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const diagram = useDiagramInfo(diagramId || undefined);
  const requestedMode = useOrgPlanningScenarioStore((state) => state.modeByDiagram[diagramId]);
  const selected = useOrgPlanningScenarioStore((state) =>
    diagramId ? state.selectedByDiagram[diagramId] : undefined,
  );
  const selectScenario = useOrgPlanningScenarioStore((state) => state.select);
  const pendingHistory = useAtlasDraftSaveRecoveryStore((state) => Object.values(state.attempts).some((attempt) =>
    attempt.historyAction && attempt.scope.folderId === folderId && attempt.scope.diagramId === diagramId
    && isOrgPlanningReviewScopeCurrent(attempt.scope)));
  const pendingSelectionRefresh = useRef<{
    scenarioId: string;
    headChangeSetId: string;
  } | null>(null);
  const mode = atlasProjectionMode(diagram.data?.viewDefinition, Boolean(selected), requestedMode);
  const scenarioSelected = mode === 'scenario' && Boolean(selected);
  const organizationPlansQueryKey = useMemo(
    () => ['atlas', 'organization-plans', folderId, diagramId] as const,
    [diagramId, folderId],
  );
  const heads = useQuery({
    queryKey: organizationPlansQueryKey,
    // Not gated on a selection: the selection store is in-memory, so after a
    // reload it is empty while the draft still exists server-side. Gating the
    // list on the selection made rediscovery impossible — the one open draft a
    // chart carries would silently vanish from every surface until the next
    // gesture happened to recreate it.
    enabled: Boolean((input.enabled ?? true) && folderId && diagramId),
    queryFn: async (): Promise<Array<{
      scenarioId: string;
      headChangeSetId: string;
      operationalSuccessorChangeSetId?: string;
    }>> => {
      const response = await authenticatedFetch(
        `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans`,
      );
      if (!response.ok) {
        const error = new Error(`Failed to refresh organization scenarios (${response.status})`);
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      return response.json() as Promise<Array<{
        scenarioId: string;
        headChangeSetId: string;
        operationalSuccessorChangeSetId?: string;
      }>>;
    },
    staleTime: 5_000,
    retry: boundedRetry,
    refetchInterval: backedOffInterval,
    refetchOnWindowFocus: (query) => {
      const status = statusOf(query.state.error);
      return !(status !== undefined && TERMINAL_HTTP_STATUSES.has(status));
    },
  });
  useEffect(() => {
    // Disabled means this surface is not authorized/mounted to reconcile
    // lifecycle state. Cached successful heads from a previous access grant
    // must not mutate the in-memory selection after the boundary closes.
    if (!(input.enabled ?? true)) return;
    // Discovery cannot identify which new head acknowledges a lost history
    // response. Keep its editing epoch until that exact receipt is reconciled.
    if (pendingHistory) return;
    // A draft amendment advances the immutable scenario head before the
    // scenario-list cache refreshes. Never clear or rewind that accepted head
    // by reconciling it against stale list data.
    if (!heads.isSuccess) return;
    if (!selected) {
      if (heads.isStale || heads.isFetching) return;
      pendingSelectionRefresh.current = null;
      // Resume the chart's open draft after a reload. A single open plan is
      // unambiguous; with several, restore the one THIS tab had selected
      // before the reload (session-scoped memory) — choosing arbitrarily
      // would route the next gesture into the wrong draft, but restoring the
      // user's own prior selection is what a refresh should preserve.
      if (requestedMode === 'live') return;
      const resumableHeads = heads.data.filter((candidate) => !candidate.operationalSuccessorChangeSetId);
      if (resumableHeads.length === 1) {
        selectScenario(diagramId, resumableHeads[0], false);
      } else if (resumableHeads.length > 1) {
        const remembered = recallSelectedOrgPlanScenarioId(diagramId);
        const match = remembered
          ? resumableHeads.find((candidate) => candidate.scenarioId === remembered)
          : undefined;
        if (match) selectScenario(diagramId, match, false);
      }
      return;
    }
    const current = heads.data.find((candidate) => candidate.scenarioId === selected.scenarioId);
    if (current?.headChangeSetId === selected.headChangeSetId) {
      pendingSelectionRefresh.current = null;
      return;
    }

    const pending = pendingSelectionRefresh.current;
    const isPendingSelection = pending?.scenarioId === selected.scenarioId
      && pending.headChangeSetId === selected.headChangeSetId;
    if (isPendingSelection) {
      if (heads.isStale || heads.isFetching) return;
      // The refresh has completed with an authoritative answer. Only now may
      // discovery correct a selection that the server did not accept.
      pendingSelectionRefresh.current = null;
      if (!current) selectScenario(diagramId, null);
      else selectScenario(diagramId, current, false);
      return;
    }

    // A locally accepted head can arrive before the organization-plan list has
    // been refreshed. Preserve it and make the list authoritative before
    // correcting the selection; otherwise an old but currently fresh cache
    // entry immediately rewinds the store to the previous head.
    pendingSelectionRefresh.current = selected;
    if (!heads.isStale && !heads.isFetching) {
      void queryClient.invalidateQueries({
        queryKey: organizationPlansQueryKey,
        refetchType: 'active',
      }).catch(() => {
        const pendingSelection = pendingSelectionRefresh.current;
        if (pendingSelection?.scenarioId === selected.scenarioId
          && pendingSelection.headChangeSetId === selected.headChangeSetId) {
          pendingSelectionRefresh.current = null;
        }
      });
    }
  }, [diagramId, heads.data, heads.isFetching, heads.isStale, heads.isSuccess, input.enabled,
    pendingHistory, queryClient, organizationPlansQueryKey, requestedMode, selectScenario, selected]);
  const projection = useQuery({
    queryKey: orgPlanningScenarioProjectionKey(
      folderId,
      diagramId,
      selected?.scenarioId,
      selected?.headChangeSetId,
      input.projectionKind,
    ),
    enabled: Boolean(
      (input.enabled ?? true)
      && folderId
      && diagramId
      && scenarioSelected
      && selected?.scenarioId
      && selected.headChangeSetId,
    ),
    queryFn: async ({ signal }): Promise<OrgPlanningScenarioProjection> => {
      if (!selected) throw new Error('No organization scenario selected');
      const response = await authenticatedFetch(
        `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${selected.headChangeSetId}/projection?kind=${input.projectionKind}`,
        { signal },
      );
      if (!response.ok) {
        const error = new Error(`Failed to load organization scenario projection (${response.status})`);
        // A 409 means this plan's baseline no longer matches the graph. That is
        // terminal for the plan, not transient, so it must not be retried or
        // polled — otherwise a superseded scenario spams the console with
        // conflicts for as long as the chart stays open.
        (error as Error & { status?: number }).status = response.status;
        const body = await response.json().catch(() => null) as { error?: unknown } | null;
        if (typeof body?.error === 'string') (error as Error & { code?: string }).code = body.error;
        throw error;
      }
      return response.json() as Promise<OrgPlanningScenarioProjection>;
    },
    staleTime: 5_000,
    retry: boundedRetry,
    refetchInterval: backedOffInterval,
    refetchOnWindowFocus: (query) => {
      const status = statusOf(query.state.error);
      return !(status !== undefined && TERMINAL_HTTP_STATUSES.has(status));
    },
  });
  return {
    ...projection,
    data: scenarioSelected ? projection.data : undefined,
    error: scenarioSelected ? projection.error : null,
    mode,
    isPlanningArtifact: diagram.data?.viewDefinition?.projection === 'planning_chart',
    hasSelectedScenario: scenarioSelected,
    isDiscovering: heads.isPending && heads.fetchStatus !== 'idle',
    discoveryError: heads.error,
  };
}
